import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Buildings, Users, CreditCard, CurrencyDollar, SignOut, Storefront, Sparkle } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LanguageToggle } from "@/i18n/I18nContext";

const PIE_COLORS = { trial: "#C9A227", basic: "#2C3E35", elite: "#B86B5A" };
const STATUS_COLORS = { trialing: "#C9A227", active: "#7A8B5C", past_due: "#B86B5A", canceled: "#6B6A67" };

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E2DC] p-5">
      <div className="flex items-center justify-between">
        <p className="label-caps">{label}</p>
        <Icon size={20} weight="duotone" color={accent} />
      </div>
      <p className="font-heading text-3xl font-semibold mt-3 tracking-tight">{value}</p>
    </div>
  );
}

export default function PlatformAdmin() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const pieData = stats ? [
    { name: t.billing.trialing, key: "trial", value: stats.plan_counts.trial || 0 },
    { name: "Basic", key: "basic", value: stats.plan_counts.basic || 0 },
    { name: "Elite", key: "elite", value: stats.plan_counts.elite || 0 },
  ].filter((d) => d.value > 0) : [];

  const signups = stats ? stats.signups_daily.map((d) => ({ ...d, label: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) })) : [];

  return (
    <div className="min-h-screen bg-[#FDFBF9]">
      <header className="bg-white border-b border-[#E5E2DC] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#2C3E35] flex items-center justify-center"><Storefront size={20} weight="duotone" color="#D4AF37" /></div>
            <div>
              <div className="font-heading font-extrabold tracking-tight leading-none">BellaOS</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A67] mt-0.5">{t.admin.overline}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <span className="text-sm text-[#6B6A67] hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/login"); }} className="p-2 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF] hover:text-[#B86B5A] transition-colors"><SignOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 fade-up" data-testid="admin-page">
        <h1 className="font-heading text-3xl font-light tracking-tight mb-6">{t.admin.title}</h1>

        {!stats ? (
          <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Buildings} label={t.admin.businesses} value={stats.total_businesses} accent="#2C3E35" />
              <Stat icon={Users} label={t.admin.users} value={stats.total_users} accent="#B86B5A" />
              <Stat icon={CreditCard} label={t.admin.activeSubs} value={stats.active_subscriptions} accent="#7A8B5C" />
              <Stat icon={CurrencyDollar} label={t.admin.mrr} value={`$${stats.mrr.toLocaleString()}`} accent="#C9A227" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 bg-white rounded-xl border border-[#E5E2DC] p-6">
                <p className="label-caps mb-4">{t.admin.signups}</p>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={signups} margin={{ left: -20, right: 6 }}>
                    <defs><linearGradient id="su" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B86B5A" stopOpacity={0.25} /><stop offset="100%" stopColor="#B86B5A" stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="label" stroke="#6B6A67" fontSize={11} tickLine={false} axisLine={false} interval={4} />
                    <YAxis stroke="#6B6A67" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 13 }} />
                    <Area type="monotone" dataKey="count" stroke="#B86B5A" strokeWidth={2.5} fill="url(#su)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
                <p className="label-caps mb-4">{t.admin.planBreakdown}</p>
                {pieData.length === 0 ? <p className="text-sm text-[#6B6A67]">—</p> : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                        {pieData.map((d) => <Cell key={d.key} fill={PIE_COLORS[d.key]} />)}
                      </Pie>
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 13 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <p className="label-caps mb-4">{t.admin.recentBusinesses}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#6B6A67] border-b border-[#E5E2DC]">
                      <th className="pb-2 font-medium">{t.admin.name}</th>
                      <th className="pb-2 font-medium">{t.admin.type}</th>
                      <th className="pb-2 font-medium">{t.admin.plan}</th>
                      <th className="pb-2 font-medium">{t.admin.joined}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_businesses.map((b, i) => (
                      <tr key={i} className="border-b border-[#E5E2DC] last:border-0">
                        <td className="py-3 font-medium">{b.name}</td>
                        <td className="py-3 text-[#6B6A67] capitalize">{b.type}</td>
                        <td className="py-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${STATUS_COLORS[b.billing_status]}18`, color: STATUS_COLORS[b.billing_status] }}>{b.plan}</span></td>
                        <td className="py-3 text-[#6B6A67]">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
